import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import apiClient, { formatApiError } from "@/lib/apiClient";
import { trackDemoStart } from "@/lib/analytics";
import { PROJECT_TYPES } from "@/lib/status";
import UsageBanner from "@/components/UsageBanner";
import { UploadSimple, FileText, CircleNotch, Sparkle } from "@phosphor-icons/react";

const Field = ({ label, children }) => (
  <div>
    <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);

const inputCls =
  "w-full h-11 px-3.5 rounded-md border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition";

export default function NewReviewPage() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [form, setForm] = useState({
    client_name: "",
    project_name: "",
    proposal_value: "",
    project_type: "Control4",
    salesperson: "",
    engineer: "",
    project_manager: "",
    expected_start_date: "",
    expected_completion_date: "",
    proposal_text: "",
    reviewer_notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setFileName(file.name);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await apiClient.post("/extract-text", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, proposal_text: res.data.text }));
      toast.success("Text extracted from file");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not read file");
      setFileName("");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.project_name) {
      toast.error("Client name and project name are required");
      return;
    }
    if (!form.proposal_text.trim()) {
      toast.error("Upload a file or paste the proposal text");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, proposal_value: parseFloat(form.proposal_value) || 0 };
      const res = await apiClient.post("/proposals", payload);
      if (res.data.is_demo_analysis) trackDemoStart(res.data.id);
      toast.success("Review started — analyzing proposal");
      navigate(`/review/${res.data.id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        const d = err.response.data?.detail || {};
        toast.error(d.message || "You've reached your plan limit.");
        navigate("/pricing");
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="mb-8">
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-[#0F172A]">New Proposal Review</h1>
        <p className="text-slate-500 mt-2">Enter project details, then upload or paste the proposal for AI analysis.</p>
      </div>

      <div className="mb-6"><UsageBanner compact /></div>

      <form onSubmit={submit} className="space-y-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-display font-bold text-lg text-[#0F172A] mb-5">Project Information</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Client Name">
              <input data-testid="field-client-name" className={inputCls} value={form.client_name} onChange={set("client_name")} placeholder="Hamilton Residence" />
            </Field>
            <Field label="Project Name">
              <input data-testid="field-project-name" className={inputCls} value={form.project_name} onChange={set("project_name")} placeholder="Whole-home automation" />
            </Field>
            <Field label="Proposal Value ($)">
              <input data-testid="field-proposal-value" type="number" className={inputCls} value={form.proposal_value} onChange={set("proposal_value")} placeholder="250000" />
            </Field>
            <Field label="Project Type">
              <select data-testid="field-project-type" className={inputCls} value={form.project_type} onChange={set("project_type")}>
                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Salesperson">
              <input data-testid="field-salesperson" className={inputCls} value={form.salesperson} onChange={set("salesperson")} />
            </Field>
            <Field label="Engineer">
              <input data-testid="field-engineer" className={inputCls} value={form.engineer} onChange={set("engineer")} />
            </Field>
            <Field label="Project Manager">
              <input data-testid="field-project-manager" className={inputCls} value={form.project_manager} onChange={set("project_manager")} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Start Date">
                <input data-testid="field-start-date" type="date" className={inputCls} value={form.expected_start_date} onChange={set("expected_start_date")} />
              </Field>
              <Field label="Completion">
                <input data-testid="field-completion-date" type="date" className={inputCls} value={form.expected_completion_date} onChange={set("expected_completion_date")} />
              </Field>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-display font-bold text-lg text-[#0F172A] mb-5">Proposal Content</h2>

          <div className="flex items-center gap-3 mb-4">
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" onChange={handleFile} className="hidden" data-testid="file-input" />
            <button
              type="button"
              data-testid="upload-btn"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 h-10 px-4 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              {uploading ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} weight="bold" />}
              Upload PDF / DOCX / TXT
            </button>
            {fileName && (
              <span className="inline-flex items-center gap-1.5 text-sm text-slate-500">
                <FileText size={15} /> {fileName}
              </span>
            )}
          </div>

          <Field label="Proposal Text">
            <textarea
              data-testid="field-proposal-text"
              value={form.proposal_text}
              onChange={set("proposal_text")}
              rows={12}
              placeholder="Paste the full proposal text here, or upload a file above…"
              className="w-full px-3.5 py-3 rounded-md border border-slate-300 bg-white text-sm leading-relaxed focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition font-mono-plex"
            />
          </Field>

          <div className="mt-5">
            <Field label="Notes from Reviewer">
              <textarea
                data-testid="field-reviewer-notes"
                value={form.reviewer_notes}
                onChange={set("reviewer_notes")}
                rows={3}
                placeholder="Optional context for the AI reviewer…"
                className="w-full px-3.5 py-3 rounded-md border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition"
              />
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pb-10">
          <button type="button" onClick={() => navigate("/")} className="h-11 px-5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button
            data-testid="run-review-btn"
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 h-11 px-6 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors disabled:opacity-60"
          >
            {submitting ? <><CircleNotch size={17} className="animate-spin" /> Analyzing proposal…</> : <><Sparkle size={17} weight="fill" /> Run AI Review</>}
          </button>
        </div>
      </form>
    </div>
  );
}
